import { ICompleteData, IQuoteAction, IQuoteActions, IQuoteState } from './models';
import * as api from '../../data/api/api';
import * as uuid from 'uuid/v4';
import { IPolicyApplication, IPremium, IProduct, IQuotes } from '../../data/api/models';
import { IStore } from '../../redux/IStore';
import { change, getFormValues, change as formChange } from 'redux-form';
import { forEach, cloneDeep, get, filter } from 'lodash';

const initialState: IQuoteState = {
  isCalculating: false,
  initialised: false,
  application: {
    id: '',
    quoteReference: '',
    tripType: null,
    groupType: null,
    startDate: '',
    endDate: '',
    destinations: [],
    activities: null,
    region: '',
    quotationDeclaration: false,
    residency: '',
    travellers: [
      {
        id: uuid(),
        title: '',
        firstName: '',
        lastName: '',
        dob: '',
        phone: '',
        email: '',
        address: {
          line1: '',
          line2: '',
          town: '',
          county: '',
          postcode: '',
        },
        type: 'adult',
        screening: null,
        screeningSessionId: '',
      },
    ],
    medicalDeclaration: [],
    schemeId: '',
    payOnAccount: false,
    steps: ['tripDetails'],
    calculateAllSchemes: false,
  },
  destinations: [],
  quotes: {},
  policy: null,
  complete: null,
  errorMessage: null,
  isRetrievingQuote: false,
  signpost: null,
};

const INIT = 'b2c/quote/INIT';
const SET_STEPS = 'b2c/quote/SET_STEPS';
const SET_APPLICATION = 'b2c/quote/SET_APPLICATION';
const SET_DESTINATIONS = 'b2c/quote/SET_DESTINATIONS';
const SET_IS_CALCULATING = 'b2c/quote/SET_IS_CALCULATING';
const COMPLETE_DOCUMENTS = 'b2c/quote/COMPLETE_DOCUMENTS';
const SET_QUOTES = 'b2c/quote/SET_QUOTES';
const SET_QUOTE_STEPS = 'b2c/quote/SET_QUOTE_STEPS';
const SET_POLICY = 'b2c/quote/SET_POLICY';
const COMPLETE = 'b2c/quote/COMPLETE';
const RESET = 'b2c/quote/RESET';
const UPDATE_STEPS = 'b2c/quote/UPDATE_STEPS';
const SET_ERROR_MESSAGE = 'b2c/quote/SET_ERROR_MESSAGE';
const DISPATCH_CAMPAIGN_CODE = 'b2c/quote/DISPATCH_CAMPAIGN_CODE';
const VOUCHER_CODE_SUCCESS = 'b2c/quote/VOUCHER_CODE_SUCCESS';
const VOUCHER_CODE_ERROR = 'b2c/quote/VOUCHER_CODE_ERROR';
const VOUCHER_CODE_RESET = 'b2c/quote/VOUCHER_CODE_RESET';
const SET_CAMPAIGN_CODE = 'b2c/quote/SET_CAMPAIGN_CODE';
const RETRIEVE_QUOTE = 'b2c/quote/RETRIEVE_QUOTE';
const SET_SIGNPOST = 'b2c/quote/SET_SIGNPOST';

const retrieveQuote = (quote) => (dispatch) => {
  return dispatch({ type: RETRIEVE_QUOTE, payload: { quote } });
};

export function reducer(state: IQuoteState = initialState, action: IQuoteAction): IQuoteState {
  switch (action.type) {
    case INIT:
      return {
        ...state,
        initialised: true,
        application: {
          ...action.payload,
        },
      };
    case SET_DESTINATIONS:
      return {
        ...state,
        destinations: action.payload.destinations,
      };
    case SET_APPLICATION:
      return {
        ...state,
        application: action.payload.application,
      };
    case SET_IS_CALCULATING:
      return {
        ...state,
        isCalculating: action.payload.isCalculating,
      };
    case SET_QUOTES:
      return {
        ...state,
        quotes: action.payload.quotes,
      };
    case SET_QUOTE_STEPS:
      return {
        ...state,
        application: {
          ...state.application,
          steps: action.payload,
        },
      };
    case SET_POLICY:
      return {
        ...state,
        policy: action.payload.policy,
      };
    case COMPLETE:
      return {
        ...state,
        complete: action.payload,
        errorMessage: null,
      };
    case COMPLETE_DOCUMENTS:
      return {
        ...state,
        complete: {
          ...state.complete,
          documents: action.payload,
        },
      };
    case SET_STEPS:
      return {
        ...state,
        application: {
          ...state.application,
          steps: action.payload.steps,
        },
      };
    case UPDATE_STEPS:
      return {
        ...state,
        application: {
          ...state.application,
          steps: action.payload,
        },
      };
    case SET_ERROR_MESSAGE:
      return {
        ...state,
        errorMessage: action.payload.message,
      };
    case SET_CAMPAIGN_CODE:
      return {
        ...state,
        campaignCode: action.payload.campaignCode,
      };
    case DISPATCH_CAMPAIGN_CODE:
      return {
        ...state,
        application: {
          ...state.application,
          campaignCode: action.payload,
        },
      };
    case RETRIEVE_QUOTE:
      return {
        ...state,
        isRetrievingQuote: true,
        application: {
          ...state.application,
          ...action.payload.quote,
        },
      };
    case VOUCHER_CODE_SUCCESS:
      return {
        ...state,
        voucherCodeStatus: 'success',
        voucherCodeMessage: 'Voucher code applied',
      };
    case VOUCHER_CODE_ERROR:
      return {
        ...state,
        application: {
          ...state.application,
          campaignCode: '',
        },
        voucherCodeStatus: 'error',
        voucherCodeMessage: 'Voucher code not found',
      };
    case VOUCHER_CODE_RESET:
      return {
        ...state,
        voucherCodeStatus: '',
        voucherCodeMessage: '',
      };
    case SET_SIGNPOST:
      return {
        ...state,
        signpost: action.payload,
      };
    case RESET:
      return cloneDeep(initialState);
    default:
      return state;
  }
}

const submitForm = (formValues, calculate = false, fromReduxFormValues = false) => (dispatch, getState) => {
  const state: IStore = getState();
  const { branding, quote } = state;

  let values = formValues ? formValues : quote.application;

  if (fromReduxFormValues) {
    values = getFormValues('quote')(state);
  }

  let brokerId = null;

  if (branding.trackingLink && branding.trackingLink.status === 'active') {
    brokerId = branding.trackingLink.id;
  }

  const promise = quote.application.id ?
    api.updatePolicyApplication(values, { calculate })
    : api.createPolicyApplication(formValues, branding.channel.id, brokerId);

  return promise
    .then((application: IPolicyApplication) => {
      forEach(application, (value, key) => dispatch(change('quote', key, value)));

      dispatch(setApplication(application));
    });
};

const saveQuote = (calculate = false) => (dispatch, getState) => {
  const state: IStore = getState();
  const { branding } = state;
  const values = getFormValues('quote')(state) as IPolicyApplication;

  const promise = api.saveQuote(values, branding.channel.id, { calculate });

  return promise
    .then((application: IPolicyApplication) => {
      forEach(application, (value, key) => dispatch(change('quote', key, value)));

      dispatch(setApplication(application));
    });
};

const calculate = (formValues = null, fromReduxFormValues = false) => (dispatch, getState) => {
  const state: IStore = getState();
  const { quote, router } = state;
  let values = formValues ? formValues : quote.application;
  const page = get(router, 'location.pathname', '');

  dispatch(setIsCalculating(true));

  if (fromReduxFormValues) {
    values = getFormValues('quote')(state);
  }

  return Promise.resolve(dispatch(getQuotesAndSignposting(values, page)))
    .then((quotes: any) => {
      dispatch(setQuotes(quotes));
      dispatch(setIsCalculating(false));
    }).catch(() => {
      dispatch(setIsCalculating(false));
    });
};

export function getQuotesAndSignposting(formValues, page) {
  return (dispatch, getState) => {
    dispatch(setSignpost(null));
    return api.updatePolicyApplication(formValues, { calculate: true }).then((application: IPolicyApplication) => {

      // if (application.quotes && get(router, 'location.pathname', '') === '/scheme-choice') {
      if (application.quotes) {
        if (page === '/scheme-choice') {
          const quotes = processQuotes(application.quotes);
          if (quotes.signposted) {
            // get sign posting
            return Promise.resolve(dispatch(api.getSignPosting(quotes.signposted, application.id)))
              .then((resp) => {
                dispatch(formChange('quote', 'signposted', quotes.signposted));
                dispatch(setSignpost(resp.data.data.attributes.content));
                return application.quotes;
              });
          }
        }

        dispatch(formChange('quote', 'signposted', ''));
        return application.quotes;
      }

    });
  };
}

const processQuotes = (quotes) => {
  const sortedquotes = {
    quotes: [],
    signposted: null,
  };

  const productName = Object.keys(quotes)[0];
  const calculatedQuotes = quotes[productName] || null;

  if (!calculatedQuotes) {
    return;
  }

  const quotesForSignposting = filter(calculatedQuotes, (quote: IPremium) => {
    const errors = get(quote, 'errors', []);
    const hasErrors = errors && !!errors.length;
    const declines = get(quote, 'declines', []);
    const hasDeclines = declines && !!declines.length;
    const informationMedical = get(quote, 'information.medical', []);
    if (!hasErrors) {
      sortedquotes.quotes.push(quote);
    }

    const hasMedicalExclusion = errors && errors.find((error) => {
      return error.includes('Exceeds maximum medical score') ||
        error.includes('Medical Condition has annual exclusion') ||
        error.includes('Medical Condition has winter sports exclusion') ||
        error.includes('Medical Declaration Question 4 is not covered') ||
        error.includes('Medical Declaration Question 5 is not covered') ||
        error.includes('Medical Exclusion is not allowed') ||
        error.includes('Max age for winter sports');
    });

    const hasInfoMedicalExclusion = informationMedical.length > 0;
    const hasMedicalErrors = (hasErrors && hasMedicalExclusion) || hasInfoMedicalExclusion;
    return (!hasErrors || hasMedicalErrors) && !hasDeclines;

  });

  if (!quotesForSignposting.length) {
    sortedquotes.signposted = false;
    return sortedquotes;
  }

  const signposting = quotesForSignposting.reduce((carry, quote) => {
    const premiumSignposted = get(quote, 'isSignposted', false);
    const errors = get(quote, 'errors', []);
    const informationMedical = _.get(quote, 'information.medical', []);
    const hasScreeningError = hasScreeningErrors(errors);
    const hasMedicalDecline = hasMedicalDeclines(errors);
    const hasMedicalExclusion = hasMedicalExclusions(informationMedical);
    carry.screening_errors = carry.screening_errors && hasScreeningError;
    carry.medical_declines = carry.medical_declines && hasMedicalDecline;
    carry.medical_premiums = carry.medical_premiums && (!hasScreeningError && !hasMedicalDecline && premiumSignposted);
    carry.medical_exclusions = carry.medical_exclusions &&
      (!hasScreeningError && !hasMedicalDecline && hasMedicalExclusion);

    return carry;

  }, {
    medical_premiums: true,
    screening_errors: true,
    medical_declines: true,
    medical_exclusions: true,
  });

  if (signposting.medical_premiums) {
    sortedquotes.signposted = 'medical_premiums_b2c';
  }
  if (signposting.medical_exclusions) {
    sortedquotes.signposted = 'medical_exclusions_b2c';
  }
  if (signposting.screening_errors || signposting.medical_declines) {
    sortedquotes.signposted = 'medical_declines_b2c';
  }
  return sortedquotes;

};

const hasScreeningErrors = (errors) => {
  return errors && !!errors.find((error) => {
    return error.includes('Exceeds maximum medical score') ||
      error.includes('Medical Condition has annual exclusion') ||
      error.includes('Medical Condition has winter sports exclusion') ||
      error.includes('Max age for winter sports');
  });
};

const hasMedicalDeclines = (declines) => {
  return declines && !!declines.find((decline) => {
    return decline.includes('Medical Declaration Question 4 is not covered') ||
      decline.includes('Medical Declaration Question 5 is not covered');
  });
};

const hasMedicalExclusions = (medicalExclusions) => {
  return medicalExclusions.length > 0;
};

const setDestinations = (destinations): IQuoteAction => ({ type: SET_DESTINATIONS, payload: { destinations } });
const setApplication = (application: IPolicyApplication): IQuoteAction => (
  { type: SET_APPLICATION, payload: { application } }
);
const setQuotes = (quotes: IQuotes): IQuoteAction => ({ type: SET_QUOTES, payload: { quotes } });
const reset = (): IQuoteAction => ({ type: RESET });
const error = (message: any): IQuoteAction => ({ type: SET_ERROR_MESSAGE, payload: { message } });
const setSignpost = (signpostContent: string): IQuoteAction => ({ type: SET_SIGNPOST, payload: signpostContent });

const addStepToWizard = (step: string, formOnly = true) => (dispatch, getState) => {
  const state: IStore = getState();
  const currentSteps = state.quote.application.steps;

  if (currentSteps.indexOf(step) > -1) {
    return null;
  }

  const steps = [
    ...currentSteps,
    step,
  ];

  dispatch(change('quote', 'steps', steps));

  if (!formOnly) {
    dispatch({ type: SET_STEPS, payload: { steps } });
  }
};

const updateSteps = (steps) => ({ type: UPDATE_STEPS, payload: steps });

const setIsCalculating = (isCalculating: boolean): IQuoteAction =>
  ({ type: SET_IS_CALCULATING, payload: { isCalculating } });

const loadDestinations = () => (dispatch) => {
  return api.listDestinations()
    .then((destinations) => {
      dispatch(setDestinations(destinations));
    });
};

const purchase = (application) => () => {
  return api.issuePolicyApplication(application)
    .then((data) => {
      return data;
    });
};

const complete = (data: ICompleteData): IQuoteAction => ({
  type: COMPLETE,
  payload: {
    ...data,
  },
});

const completeDocuments = (data: ICompleteData): IQuoteAction => ({
  type: COMPLETE_DOCUMENTS,
  payload: {
    ...data,
  },
});

export const getInitValues = (product: IProduct) => {
  const tripType = Object.keys(product.metadata.trip_type)[0];
  const groupType = Object.keys(product.metadata[`group_type_trip_${tripType}`])[0];

  return {
    ...cloneDeep(initialState.application),
    tripType,
    groupType,
  };
};

const initFromProduct = (product: IProduct): IQuoteAction => {
  const data = getInitValues(product);
  return {
    type: INIT,
    payload: {
      ...data,
    },
  };
};

const pollPolicyDocuments = (documentsPollToken) => () => {
  return api.documentsPoll(documentsPollToken);
};

const dispatchVoucherCode = (campaignCode) => (dispatch) => {
  dispatch({ type: DISPATCH_CAMPAIGN_CODE, payload: campaignCode });
  dispatch(checkVoucherCode(campaignCode));
};

const checkVoucherCode = (campaignCode) => (dispatch, getState) => {
  const state: IStore = getState();
  const { branding } = state;

  return api.checkCampaignCode(campaignCode, branding.channel.id)
    .then((response) => {
      if (response.data) {
        dispatch(voucherCodeSuccess());
      }
    })
    .catch((e) => {
      console.log('e', e);

      dispatch({ type: VOUCHER_CODE_ERROR });
      setTimeout((() => {
        dispatch({ type: VOUCHER_CODE_RESET });
      }), 3000);
    });
};

const voucherCodeSuccess = () => {
  return {
    type: VOUCHER_CODE_SUCCESS,
  };
};

const setCampaignCode = (campaignCode: string): IQuoteAction => ({
  type: SET_CAMPAIGN_CODE,
  payload: { campaignCode },
});

const setSteps = (steps) => ({ type: SET_QUOTE_STEPS, payload: steps });

export const actions: IQuoteActions = {
  submitForm,
  loadDestinations,
  initFromProduct,
  calculate,
  purchase,
  complete,
  completeDocuments,
  reset,
  retrieveQuote,
  error,
  pollPolicyDocuments,
  saveQuote,
  addStepToWizard,
  updateSteps,
  dispatchVoucherCode,
  checkVoucherCode,
  setCampaignCode,
  setSteps,
};
